import type {
  ProviderIntegrationOperation,
  ProviderIntegrationState,
} from '@/api/core/provider-integration-state';
import { isApiError } from '@/api/http/errors';
import { InlineLoading } from '@/components/inline-loading';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Button, DetailPage, Skeleton } from '@mgrvtechgroup/admin-ds';

function stateLabel(operation: ProviderIntegrationOperation) {
  switch (operation.terminal_state) {
    case 'PENDING':
      return '변경 결과 확인 중';
    case 'UNKNOWN':
      return '변경 결과 확인 필요';
    case 'APPLIED':
      return '기록된 결과: 적용됨';
    case 'NOT_APPLIED':
      return '기록된 결과: 미적용';
  }
}

function errorLabel(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '이 지점의 연동 정보를 확인할 권한이 없습니다.';
    if (error.status === 404) return '이 지점의 연동 정보를 찾을 수 없습니다.';
    if (error.status === 409 && error.code === 'PROPERTY_SOURCE_BINDING_CHANGED')
      return '지점 정보가 변경되어 연동 정보를 다시 확인해 주세요.';
    if (error.status === 503 && error.code === 'PROVIDER_INTEGRATION_STATE_UNAVAILABLE')
      return '연동 정보를 지금 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    if (error.kind === 'invalid-response') return '연동 정보를 확인할 수 없습니다.';
  }
  return '연동 정보를 불러오지 못했습니다.';
}

/**
 * 연동 요약 세 칸. 받는 동안에는 값 자리만 스켈레톤으로 두어, 받은 뒤 아래 섹션이 밀리지 않게 한다(칸·높이가 같다).
 */
function IntegrationSummary({ data }: { data: ProviderIntegrationState | undefined }) {
  const cells = [
    [
      '이지렘스 프로젝트(지점) 코드',
      data && (data.settings.project_mapping_configured ? '설정됨' : '설정되지 않음'),
    ],
    [
      '객실 유형 변경 결과',
      data && (data.support_status === 'SUPPORTED' ? '조회 지원' : '조회 미지원'),
    ],
    ['환경', data && (data.environment === 'dev' ? '개발' : '운영')],
  ] as const;
  return (
    <dl className="grid gap-3 sm:grid-cols-3" aria-hidden={data ? undefined : true}>
      {cells.map(([label, value]) => (
        <div key={label}>
          <dt className="caption text-muted-foreground">{label}</dt>
          <dd className="body">{value ?? <Skeleton className="h-5 w-20" />}</dd>
        </div>
      ))}
    </dl>
  );
}

function OperationCard({ operation }: { operation: ProviderIntegrationOperation }) {
  return (
    <li className="border-border flex min-w-0 flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="title-sm">
          {operation.target_kind === 'MIDTERM' ? '중기 객실 유형' : '일반 객실 유형'}
        </h4>
        <span className="body">{stateLabel(operation)}</span>
      </div>
      <p className="body text-muted-foreground">
        변경 시각 · {formatDateTimeWithOffset(operation.updated_at)}
      </p>
    </li>
  );
}

export function ProviderIntegrationStateSection({
  data,
  isFetching,
  isError,
  error,
  onRefresh,
}: {
  data: ProviderIntegrationState | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  onRefresh: () => void;
}) {
  return (
    <DetailPage.Section
      aria-label="이지렘스 연동 정보"
      title="이지렘스 연동 정보"
      description="저장된 설정과 변경 이력입니다. 현재 이지렘스 객실 조회 결과와는 별개입니다."
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={onRefresh}
          loading={isFetching}
          focusableWhenDisabled
        >
          다시 조회
        </Button>
      }
    >
      {isFetching ? (
        <>
          <IntegrationSummary data={undefined} />
          <InlineLoading>연동 정보를 확인하고 있습니다.</InlineLoading>
        </>
      ) : isError || !data ? (
        <p role="alert" className="body text-warning-foreground" aria-live="polite">
          {errorLabel(error)}
        </p>
      ) : (
        <>
          <IntegrationSummary data={data} />
          {data.operations.length === 0 ? (
            <p role="status" className="body text-muted-foreground">
              {data.support_status === 'SUPPORTED'
                ? '표시할 변경 결과가 없습니다.'
                : '이 환경에서는 변경 결과를 확인할 수 없습니다.'}
            </p>
          ) : (
            <ol className="flex min-w-0 flex-col gap-3" aria-label="최근 객실 유형 변경 결과">
              {data.operations.map((operation, index) => (
                <OperationCard
                  key={`${operation.created_at}-${String(index)}`}
                  operation={operation}
                />
              ))}
            </ol>
          )}
        </>
      )}
    </DetailPage.Section>
  );
}
